/**
 * ASR comparison API client.
 *
 * Mirrors the backend `AsrComparisonModule` shape: pull a sentence pool
 * from the user's enrolled courses, then submit recorded audio to the
 * /transcribe endpoint which fans it out to OpenAI Whisper / Replicate
 * WhisperX / Groq Whisper-large-v3-turbo in parallel.
 */
import apiClient from './apiClient';

export type AsrServiceId =
  | 'openai-whisper'
  | 'groq-whisper'
  | 'azure-speech'
  | 'deepgram';

export type DiffOp = 'match' | 'substitute' | 'delete' | 'insert';

export interface DiffEntry {
  ref?: string;
  hyp?: string;
  op: DiffOp;
}

export interface DiffSummary {
  totalRefWords: number;
  matches: number;
  substitutions: number;
  deletions: number;
  insertions: number;
  wer: number;
  entries: DiffEntry[];
}

export interface AsrWord {
  word: string;
  start?: number;
  end?: number;
  /** Deepgram-only; Whisper/Groq don't expose per-word confidence. */
  confidence?: number;
}

export interface AsrResult {
  service: AsrServiceId;
  ok: boolean;
  latencyMs: number;
  transcript?: string;
  words?: AsrWord[];
  durationSec?: number;
  costUsdMicros?: number;
  model?: string;
  error?: string;
  azureAssessment?: AzurePronunciationData;
}

export interface AzurePronunciationData {
  overallScore?: number;
  accuracyScore?: number;
  fluencyScore?: number;
  completenessScore?: number;
  prosodyScore?: number;
  pronunciationScore?: number;
  words: Array<{
    word: string;
    accuracyScore: number;
    errorType: string;
    phonemes?: Array<{ phoneme: string; accuracyScore: number }>;
    suggestion?: string;
  }>;
}

export interface ServiceComparison {
  result: AsrResult;
  diff?: DiffSummary;
}

export interface AsrSentence {
  segmentId: string;
  courseId: string;
  courseTitle: string;
  sentenceIndex: number;
  text: string;
  audioUrl: string;
  translation: string | null;
}

export interface SentencesResponse {
  sentences: AsrSentence[];
}

export interface TranscribeResponse {
  services: ServiceComparison[];
  referenceText: string;
}

export const asrCompareService = {
  async getSentences(courseIds?: string[]): Promise<SentencesResponse> {
    const params: Record<string, string> = {};
    if (courseIds && courseIds.length > 0) params.courseIds = courseIds.join(',');
    const res = await apiClient.get<SentencesResponse>('/asr-comparison/sentences', { params });
    return res.data;
  },

  async transcribeAll(
    audioBase64: string,
    mimeType: string,
    referenceText: string,
    /** Browser-measured audio duration (sec). Whisper / Groq report
     *  duration themselves, but Azure doesn't — passing it through lets
     *  the Azure cost estimate use real audio length instead of round-
     *  trip latency, which would systematically under-bill it. */
    audioDurationSec?: number,
  ): Promise<TranscribeResponse> {
    const res = await apiClient.post<TranscribeResponse>(
      '/asr-comparison/transcribe',
      { audioBase64, mimeType, referenceText, audioDurationSec },
      { timeout: 180_000 },
    );
    return res.data;
  },

  async coach(payload: CoachRequest): Promise<CoachResult> {
    const res = await apiClient.post<CoachResult>(
      '/asr-comparison/coach',
      payload,
      { timeout: 30_000 },
    );
    return res.data;
  },
};

export interface CoachIssuePayload {
  category: string;
  severity: 'low' | 'medium' | 'high';
  title: string;
  evidenceLine: string;
  refKey?: string;
  refEntry?: { why: string; tip: string; examples: string };
}

export interface CoachRequest {
  referenceText: string;
  transcript?: string;
  prosody: {
    score: number;
    pitchRangeSt: number;
    pitchVarianceSt: number;
    voicedRatio: number;
  };
  issues: CoachIssuePayload[];
}

export interface CoachTip {
  issue: string;
  advice: string;
}

export interface CoachResult {
  encouragement: string;
  tips: CoachTip[];
}
